import { useCallback, useEffect, useRef, useState } from 'react';
import { HouseholdJoinPanel } from './HouseholdJoinPanel';
import { HouseholdAdminPanel } from './HouseholdAdminPanel';
import { AccountExportPanel } from './AccountExportPanel';
import { AccountRestorePanel } from './AccountRestorePanel';
import {AccountHouseholdDraftPanel} from './AccountHouseholdDraftPanel';
import {ArchiveStoragePanel} from './ArchiveStoragePanel';
import type {HouseholdDrafts} from './accountHouseholdDrafts';
import { ManagedAccountingPanel } from './ManagedAccountingPanel';
import { useOutletContext } from 'react-router-dom';
import { api, calculatorPrompt, saveSettings, settings } from './api';
import { asArray, asRecord, stringValue, type ApiRecord, type Settings } from './types';
import { AuthRequired, PageHeader } from './PropertyPages';
import type { WorkspaceContext } from './WorkspaceShell';
import { isDelegated, type AuthSession } from '../auth';

const profileFields = [
  ['currentHousing', 'Aktuelles Wohnen', 'z. B. Miete, Fläche, Kündigungs- oder Zeitdruck'],
  ['energyAndLoadProfile', 'Energie & Lastprofil', 'Heizverhalten, PV-/Ladebedarf, Komfort'],
  ['familyAndDailyLife', 'Familie & Alltag', 'Arbeitswege, Betreuung, Räume und Alltag'],
  ['locationPriorities', 'Lageprioritäten', 'ÖPNV, Schule, Einkauf, Umfeld'],
  ['hardConstraints', 'Harte Kriterien', 'Ein Kriterium pro Zeile'],
  ['notes', 'Weitere Hinweise', 'Nur Informationen, die eine Entscheidung beeinflussen']
] as const;

const coreFinancialFields = [
  ['availableEquity', 'Verfügbares Eigenkapital', '€', 1_000],
  ['minimumReserve', 'Mindestreserve', '€', 1_000],
  ['existingDepotMarketValue', 'ETF-Depot', '€', 1_000],
  ['currentRentSq', 'Aktuelle Wohnkosten', '€/Monat', 10],
  ['savingsBeforeHousingMonthly', 'Sparfähigkeit vor Wohnen', '€/Monat', 50],
  ['loanInterestRate', 'Sollzins', '%', 0.1],
  ['initialRepaymentRate', 'Anfangstilgung', '%', 0.1],
  ['fixedRateYears', 'Zinsbindung', 'Jahre', 1],
  ['refinanceInterestRate', 'Refinanzierung Basis', '%', 0.1],
  ['refinanceStressRate', 'Refinanzierung Stress', '%', 0.1],
  ['refinanceRepaymentRate', 'Tilgung nach Refinanzierung', '%', 0.1],
  ['etfReturnRate', 'ETF-Rendite', '%', 0.1]
] as const;

const advancedFinancialFields = [
  ['cashReserveTarget', 'Zielreserve', '€', 1_000],
  ['existingDepotUnrealizedGain', 'Unrealisierter ETF-Gewinn', '€', 1_000],
  ['currentHeatingCostMonthly', 'Aktuelle Heizkosten', '€/Monat', 10],
  ['retirementYear', 'Rentenbeginn ab Jahr', 'Jahre', 1],
  ['postRetirementSavingsBeforeHousingMonthly', 'Sparfähigkeit ab Rente', '€/Monat', 50],
  ['minimumBuyPathSavingsMonthly', 'Mindestsparrate Kaufpfad', '€/Monat', 50],
  ['comparisonInitialRepaymentRate', 'Vergleichstilgung', '%', 0.1],
  ['etfTaxRate', 'ETF-Steuer auf Gewinne', '%', 0.1],
  ['inflationRate', 'Inflation', '%', 0.1],
  ['savingsReturnRate', 'Tagesgeldrendite', '%', 0.1],
  ['rentGrowthRate', 'Mietwachstum', '%', 0.1],
  ['ownerCostsGrowthRate', 'Wachstum Eigentümerkosten', '%', 0.1],
  ['recurringCapexGrowthRate', 'Wachstum Instandhaltung', '%', 0.1],
  ['propertyAppreciationRate', 'Wertentwicklung Immobilie', '%', 0.1],
  ['purchaseCostRate', 'Kaufnebenkosten', '%', 0.1],
  ['fcfGrowthRate', 'Wachstum freier Mittel', '%', 0.1],
  ['savingsCapacityGrowthRate', 'Wachstum Sparfähigkeit', '%', 0.1]
] as const;

type Household = {
  id?: string;
  name?: string;
  members?: Array<{ userId: string; email?: string; name?: string; role: string }>;
};
type AccountToken = {
  id: string;
  name?: string;
  prefix?: string;
  revoked?: boolean;
};

export function ProfilePage() {
  const user = useOutletContext<WorkspaceContext>().user;
  const identity=!user?'signed-out':isDelegated(user)?`delegated:${user.principal.userId}:${user.principal.householdId}`:`oidc:${user.profile.iss??''}:${user.profile.sub}`;
  return <ProfileForUser key={identity} user={user}/>;
}
function ProfileForUser({user}:{user:AuthSession|null}) {
  const delegated = isDelegated(user);
  const accountSession=useRef(user);
  const accessRequest = useRef<object | null>(null);
  useEffect(()=>{accountSession.current=user;return()=>{accountSession.current=null;};},[user]);
  const [loaded, setLoaded] = useState<Settings | null>(null);
  const [profile, setProfile] = useState<ApiRecord>({});
  const [financialDefaults, setFinancialDefaults] = useState<ApiRecord>({});
  const [household, setHousehold] = useState<Household | null>(null);
  const [tokens, setTokens] = useState<AccountToken[]>([]);
  const [prompt, setPrompt] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [joinPending,setJoinPending]=useState(false);
  const joinPendingChange=useCallback((pending:boolean)=>setJoinPending(pending),[]);
  const [adminPending,setAdminPending]=useState(false);
  const adminPendingChange=useCallback((pending:boolean)=>setAdminPending(pending),[]);
  const [restorePending,setRestorePending]=useState(false);
  const restorePendingChange=useCallback((pending:boolean)=>setRestorePending(pending),[]);
  const [draftPending,setDraftPending]=useState(false);
  const [retentionPending,setRetentionPending]=useState(false),[archiveRevision,setArchiveRevision]=useState(0);
  const archiveRemoved=useCallback(()=>setArchiveRevision(n=>n+1),[]);
  const accountPending=joinPending||adminPending||restorePending||draftPending||retentionPending;
  const [exportPending,setExportPending]=useState(false);
  const exportPendingChange=useCallback((pending:boolean)=>setExportPending(pending),[]);
  const [generatedSecret, setGeneratedSecret] = useState('');

  const loadAccount = async () => {
    if (!user) return;
    const [settingsValue, householdValue, tokenValue] = await Promise.all([
      settings(user),
      delegated ? Promise.resolve({}) : api(user, '/api/account/household'),
      delegated ? Promise.resolve([]) : api(user, '/api/account/tokens')
    ]);
    if(accountSession.current!==user)throw new Error('Anmeldung hat gewechselt. Haushaltsdaten wurden nicht ersetzt.');
    setLoaded(settingsValue);
    setProfile(settingsValue.evaluationProfile ?? {});
    setFinancialDefaults(settingsValue.financialDefaults ?? {});
    setHousehold(asRecord(householdValue) as Household);
    setTokens(asArray(tokenValue).map(value => asRecord(value) as AccountToken));
  };

  useEffect(() => {
    if (!user) return;
    let active = true;
    Promise.all([
      settings(user),
      delegated ? Promise.resolve({}) : api(user, '/api/account/household'),
      delegated ? Promise.resolve([]) : api(user, '/api/account/tokens')
    ]).then(([settingsValue, householdValue, tokenValue]) => {
      if (!active) return;
      setLoaded(settingsValue);
      setProfile(settingsValue.evaluationProfile ?? {});
      setFinancialDefaults(settingsValue.financialDefaults ?? {});
      setHousehold(asRecord(householdValue) as Household);
      setTokens(asArray(tokenValue).map(value => asRecord(value) as AccountToken));
    }).catch(reason => active && setNotice(reason instanceof Error ? reason.message : 'Profil konnte nicht geladen werden'));
    return () => { active = false; };
  }, [user, delegated]);

  if (!user) {
    return <AuthRequired title="Haushaltsprofil" text="Melde dich an, um gemeinsame Haushaltsannahmen, Zugänge und den Rechner-Prompt zu verwalten." />;
  }

  const updateProfile = (key: string, value: string) => setProfile(current => ({
    ...current,
    [key]: key === 'hardConstraints'
      ? value.split('\n').map(item => item.trim()).filter(Boolean)
      : value
  }));
  const updateFinancial = (key: string, value: string) => {
    const parsed = Number(value);
    setFinancialDefaults(current => ({ ...current, [key]: Number.isFinite(parsed) ? parsed : 0 }));
  };
  const acceptSettingsResponse = (response: ApiRecord) => {
    const version = typeof response.version === 'number'
      ? response.version
      : (loaded?.version ?? 0) + 1;
    setLoaded(current => ({
      version,
      financialDefaults: asRecord(response.financialDefaults ?? current?.financialDefaults),
      evaluationProfile: asRecord(response.evaluationProfile ?? current?.evaluationProfile),
      raw: response
    }));
    return version;
  };
  const saveProfile = async () => {
    setBusy('profile');
    try {
      const response = asRecord(await saveSettings(user, {
        version: loaded?.version,
        expectedHouseholdId: household?.id,
        evaluationProfile: profile
      }));
      acceptSettingsResponse(response);
      setNotice('Profil gespeichert. Künftige kanonische Rechner-Prompts verwenden diese Version.');
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : 'Speichern fehlgeschlagen. Bitte neu laden und Änderungen prüfen.');
    } finally {
      setBusy(null);
    }
  };
  const saveFinancial = async () => {
    setBusy('financial');
    try {
      const keys = [...coreFinancialFields, ...advancedFinancialFields].map(([key]) => key);
      const values = Object.fromEntries(keys.map(key => [key, financialDefaults[key]]));
      const response = asRecord(await saveSettings(user, {
        version: loaded?.version,
        expectedHouseholdId: household?.id,
        financialDefaults: values
      }));
      acceptSettingsResponse(response);
      setNotice('Finanzielle Haushaltsdefaults gespeichert. Ein neuer MCP-Start nutzt diese Version.');
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : 'Finanzielle Defaults konnten nicht gespeichert werden.');
    } finally {
      setBusy(null);
    }
  };
  const loadArchiveSettings=(value:HouseholdDrafts)=>{
    if(accountSession.current!==user||value.householdId!==household?.id||value.settings.target.version!==loaded?.version||!value.settings.draft.state||busy!==null||accountPending)throw new Error('Aktuellen Haushaltsstand erneut laden und vergleichen.');
    setProfile(value.settings.draft.state.evaluationProfile);setFinancialDefaults({...loaded.financialDefaults,...value.settings.draft.state.financialDefaults});
    setNotice('Ungeprüfte Archivannahmen als lokale Eingaben geladen. Profil und finanzielle Vorgaben getrennt prüfen und speichern.');
  };
  const showPrompt = async () => {
    setBusy('prompt');
    try {
      const response = asRecord(await calculatorPrompt(user));
      setPrompt(String(response.prompt ?? response.text ?? 'Kein Prompt im Server-Response.'));
      setNotice(`Kanonischer Prompt für Settings-Version ${String(response.settingsVersion ?? loaded?.version ?? '–')} geladen.`);
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : 'Prompt konnte nicht geladen werden');
    } finally {
      setBusy(null);
    }
  };
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} kopiert.`);
    } catch {
      setNotice('Zwischenablage nicht verfügbar.');
    }
  };
  const createInvite = async () => {
    setBusy('invite');
    try {
      const response = asRecord(await api(user, '/api/account/household/invites', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail || null })
      }));
      setGeneratedSecret(stringValue(response, 'inviteToken') ?? '');
      setNotice('Einladung erstellt. Sie ist sieben Tage gültig und wird nur jetzt vollständig angezeigt.');
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : 'Einladung konnte nicht erstellt werden.');
    } finally {
      setBusy(null);
    }
  };
  const refreshTokens = async () => {
    const tokenValue = await api(user, '/api/account/tokens');
    if (accountSession.current !== user) return;
    setTokens(asArray(tokenValue).map(value => asRecord(value) as AccountToken));
  };
  const createExtensionToken = async () => {
    if (accessRequest.current) return;
    const request = {};
    accessRequest.current = request;
    const current = () => accountSession.current === user && accessRequest.current === request;
    setBusy('token');
    try {
      const response = asRecord(await api(user, '/api/account/tokens', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Browser extension',
          scopes: ['extension:crawl', 'research:read'],
          expiresInDays: 365
        })
      }));
      if (!current()) return;
      setGeneratedSecret(stringValue(response, 'token') ?? '');
      let refreshed = true;
      try { await refreshTokens(); } catch { refreshed = false; }
      if (current()) setNotice(refreshed
        ? 'Extension-Token erstellt. Er wird nur jetzt vollständig angezeigt.'
        : 'Extension-Token erstellt. Die Token-Liste konnte nicht aktualisiert werden. Das Geheimnis ist nur jetzt vollständig sichtbar.');
    } catch (reason) {
      if (current()) setNotice(reason instanceof Error ? reason.message : 'Token konnte nicht erstellt werden.');
    } finally {
      if (accessRequest.current === request) {
        accessRequest.current = null;
        setBusy(null);
      }
    }
  };
  const revokeToken = async (id: string) => {
    if (accessRequest.current) return;
    const request = {};
    accessRequest.current = request;
    const current = () => accountSession.current === user && accessRequest.current === request;
    setBusy(`revoke:${id}`);
    try {
      await api(user, `/api/account/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!current()) return;
      setTokens(values => values.map(token => token.id === id ? { ...token, revoked: true } : token));
      let refreshed = true;
      try { await refreshTokens(); } catch { refreshed = false; }
      if (current()) setNotice(refreshed
        ? 'Token widerrufen.'
        : 'Token widerrufen. Die Token-Liste konnte nicht aktualisiert werden.');
    } catch (reason) {
      if (current()) setNotice(reason instanceof Error ? reason.message : 'Token konnte nicht widerrufen werden.');
    } finally {
      if (accessRequest.current === request) {
        accessRequest.current = null;
        setBusy(null);
      }
    }
  };

  return <section>
    <PageHeader
      eyebrow="Haushalt"
      title={household?.name ?? 'Profil & Annahmen'}
      description="Finanzielle Defaults und Entscheidungsprofil gelten für den gemeinsamen Haushalt. Research-Prompts bleiben davon strikt getrennt."
    />
    {notice ? <p className="state" role="status">{notice}</p> : null}
    {!delegated&&household?.id&&loaded?.version!==undefined&&<AccountHouseholdDraftPanel key={`household-draft:${household.id}:${archiveRevision}`} user={user} householdId={household.id} kind="settings" version={loaded.version} blocked={busy!==null||joinPending||adminPending||restorePending||exportPending||retentionPending||JSON.stringify(profile)!==JSON.stringify(loaded.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded.financialDefaults??{})} onPendingChange={setDraftPending} onLoad={loadArchiveSettings}/>}
    <div className="profile-layout">
      <section className="data-section">
        <h2>Finanzielle Haushaltsdefaults</h2>
        <p>Diese Werte laden Rechner und MCP-Start als Ausgangspunkt. Objektwerte bleiben davon getrennt.</p>
        <FinancialFields fields={coreFinancialFields} values={financialDefaults} disabled={accountPending} onChange={updateFinancial} />
        <details className="profile-advanced">
          <summary>Weitere finanzielle Defaults</summary>
          <FinancialFields fields={advancedFinancialFields} values={financialDefaults} disabled={accountPending} onChange={updateFinancial} />
        </details>
        <div className="page-actions">
          <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void saveFinancial()}>
            {busy === 'financial' ? 'Speichere…' : 'Finanzielle Defaults speichern'}
          </button>
        </div>
      </section>
      <section className="data-section">
        <h2>Entscheidungsprofil</h2>
        <div className="profile-fields">
          {profileFields.map(([key, label, hint]) => <label key={key}>
            <span>{label}</span>
            <textarea
              rows={key === 'notes' ? 5 : 3}
              placeholder={hint}
              value={Array.isArray(profile[key]) ? profile[key].join('\n') : String(profile[key] ?? '')}
              disabled={accountPending} onChange={event => updateProfile(key, event.target.value)}
            />
          </label>)}
        </div>
        <div className="page-actions">
          <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void saveProfile()}>
            {busy === 'profile' ? 'Speichere…' : 'Profil speichern'}
          </button>
        </div>
      </section>
    </div>
    <section className="data-section prompt-panel">
      <h2>Kanonischer Rechner-Prompt</h2>
      <p>Der Text wird serverseitig aus den gespeicherten Haushaltswerten gebaut und entspricht dem nächsten MCP-Start. Rechner-Vorschauen mit ungespeicherten Objektwerten bleiben Entwürfe ohne gültige startId.</p>
      <div className="page-actions">
        <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void showPrompt()}>
          {busy === 'prompt' ? 'Lade…' : 'Prompt anzeigen'}
        </button>
        {prompt ? <button className="quiet-button" type="button" onClick={() => void copy(prompt, 'Prompt')}>Kopieren</button> : null}
      </div>
      {prompt ? <pre>{prompt}</pre> : <p className="state">Noch nicht geladen.</p>}
    </section>
    {!delegated ? <section className="data-section">
      <h2>Haushalt &amp; Zugänge</h2>
      <p>{household?.members?.length ?? 1} Haushaltsmitglied(er). Einladungen und Tokens werden nur bewusst erzeugt.</p>
      <div className="account-tools-grid">
        <div>
          <h3>Person einladen</h3>
          <input type="email" placeholder="E-Mail (optional)" value={inviteEmail} onChange={event => setInviteEmail(event.target.value)} />
          <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void createInvite()}>Einladung erstellen</button>
        </div>
        <div>
          <h3>Browser-Erweiterung</h3>
          <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void createExtensionToken()}>Extension-Token erstellen</button>
          {tokens.length ? <ul className="token-list">{tokens.map(token => <li key={token.id}>
            <span><strong>{token.name ?? 'Token'}</strong><small>{token.prefix ?? '–'}…{token.revoked ? ' · widerrufen' : ''}</small></span>
            {!token.revoked ? <button type="button" disabled={busy !== null||accountPending||!loaded} onClick={() => void revokeToken(token.id)}>Widerrufen</button> : null}
          </li>)}</ul> : null}
        </div>
      </div>
      {generatedSecret ? <div className="secret-value"><code>{generatedSecret}</code><button type="button" onClick={() => void copy(generatedSecret, 'Geheimnis')}>Kopieren</button><small>Nur einmal vollständig sichtbar. Sicher speichern.</small></div> : null}
    </section> : <p className="state">Delegierter Zugang: Haushalts- und Tokenverwaltung sind nicht freigegeben.</p>}
    {!delegated&&<ManagedAccountingPanel key={household?.id??'loading'} user={user} householdId={household?.id??null} blocked={busy!==null||accountPending}/>}
    {!delegated&&<AccountRestorePanel key={`account-review:${archiveRevision}`} user={user} householdId={household?.id??null} blocked={busy!==null||joinPending||adminPending||exportPending||retentionPending||JSON.stringify(profile)!==JSON.stringify(loaded?.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded?.financialDefaults??{})} onPendingChange={restorePendingChange}/>}
    {!delegated&&<ArchiveStoragePanel user={user} householdId={household?.id??null} blocked={busy!==null||joinPending||adminPending||restorePending||draftPending||exportPending||JSON.stringify(profile)!==JSON.stringify(loaded?.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded?.financialDefaults??{})} onPendingChange={setRetentionPending} onComplete={archiveRemoved}/>}
    {!delegated&&<AccountExportPanel user={user} blocked={busy!==null||accountPending||JSON.stringify(profile)!==JSON.stringify(loaded?.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded?.financialDefaults??{})} onPendingChange={exportPendingChange}/>}
    {!delegated&&<HouseholdAdminPanel user={user} members={household?.members??[]} blocked={busy!==null||exportPending||restorePending||joinPending||retentionPending||JSON.stringify(profile)!==JSON.stringify(loaded?.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded?.financialDefaults??{})}
      onPendingChange={adminPendingChange} onComplete={async()=>{setGeneratedSecret('');setInviteEmail('');await loadAccount();}}/>}
    {!delegated&&<HouseholdJoinPanel user={user} blocked={busy!==null||exportPending||restorePending||adminPending||retentionPending||JSON.stringify(profile)!==JSON.stringify(loaded?.evaluationProfile??{})||JSON.stringify(financialDefaults)!==JSON.stringify(loaded?.financialDefaults??{})}
      onPendingChange={joinPendingChange} onComplete={async()=>{setGeneratedSecret('');setInviteEmail('');await loadAccount();}}/>}
  </section>;
}

function FinancialFields({
  fields,
  values,
  onChange,disabled=false
}: {
  fields: ReadonlyArray<readonly [string, string, string, number]>;
  values: ApiRecord;
  onChange: (key: string, value: string) => void;disabled?:boolean;
}) {
  return <div className="financial-fields">{fields.map(([key, label, suffix, step]) => <label key={key}>
    <span>{label}</span>
    <div><input type="number" disabled={disabled} step={step} value={typeof values[key] === 'number' ? String(values[key]) : ''} onChange={event => onChange(key, event.target.value)} /><small>{suffix}</small></div>
  </label>)}</div>;
}
